/**
 * Crop growth (vanilla CropBlock.randomTick) without a general random-tick
 * system: a rolling scan visits a couple of loaded chunks per game tick, finds
 * crop cells and gives each the random ticks it would have received since the
 * chunk's last visit (3/4096 per game tick), so growth rates match vanilla
 * while the per-tick cost stays bounded.
 *
 * Also the two player actions farming needs: hoe tilling (grass / dirt /
 * dirt path -> farmland, needs air above) and planting (seed item on the top
 * face of farmland). Block ids carry the growth age, so crops persist through
 * the normal block-edit save.
 */

import { BEETROOT_STAGE_IDS, CARROT_STAGE_IDS, NETHER_WART_IDS, POTATO_STAGE_IDS, WHEAT_ID, WHEAT_STAGE_IDS } from '../blocks';
import { OAK_SAPLING_ID } from '../blocks';
import { AIR, DANDELION_ID, DIRT_ID, DIRT_PATH_ID, FARMLAND_DRY_ID, FARMLAND_ID, GRASS_ID, IS_WATERY, POPPY_ID, TALL_GRASS_ID } from '../blocks';
import { CHUNK_AREA, ChunkState, type World } from './chunk';
import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { weather } from '../game/weather';
import {
  advance, boneMealStages, canSustainCrop, farmlandWeight, growChance, growthSpeed, isCrop, isFarmland, isMatureCrop, MIN_GROW_LIGHT,
  nextStage, plantedBy, RANDOM_TICK_CHANCE,
} from '../game/farming';
import { SAPLING_BONEMEAL_CHANCE, SAPLING_MIN_LIGHT, SAPLING_TICK_CHANCE, saplingEvents } from '../game/itemUse/sapling';

export interface CropHost {
  readonly world: World;
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  /** 0..1 daylight factor applied to sky light. */
  sunLevel?(): number;
}

const IS_CROP = new Uint8Array(65536);
for (let id = 0; id < 65536; id++) if (isCrop(id)) IS_CROP[id] = 1;
IS_CROP[FARMLAND_ID] = 2; IS_CROP[FARMLAND_DRY_ID] = 2;
IS_CROP[OAK_SAPLING_ID] = 3; // grows into a tree (game/itemUse/sapling.ts)

/** Chunks scanned per game tick. */
const CHUNKS_PER_TICK = 2;
/** Cap on the catch-up applied in one visit (a chunk that was far away / unloaded). */
const MAX_CATCHUP = 24000;

export class CropSim {
  private tickN = 0;
  private readonly lastVisit = new Map<number, number>();
  private queue: number[] = [];
  private qi = 0;

  constructor(private readonly host: CropHost, private readonly rnd: () => number = Math.random) {}

  tick(): void {
    this.tickN++;
    const w = this.host.world;
    for (let n = 0; n < CHUNKS_PER_TICK; n++) {
      if (this.qi >= this.queue.length) {
        this.queue = [...w.chunks.keys()];
        this.qi = 0;
        if (this.queue.length === 0) return;
      }
      const key = this.queue[this.qi++];
      const ch = w.chunks.get(key);
      if (!ch || ch.state !== ChunkState.Ready) continue;
      const last = this.lastVisit.get(key);
      this.lastVisit.set(key, this.tickN);
      if (last === undefined) continue; // first sight: start the clock
      this.scan(ch.cx, ch.cz, ch.blocks, Math.min(MAX_CATCHUP, this.tickN - last));
    }
  }

  private scan(cx: number, cz: number, blocks: Uint16Array, elapsed: number): void {
    const expected = elapsed * RANDOM_TICK_CHANCE;
    for (let i = 0; i < blocks.length; i++) {
      if (IS_CROP[blocks[i]] === 0) continue;
      const lx = i % CHUNK_SIZE, lz = ((i / CHUNK_SIZE) | 0) % CHUNK_SIZE, y = (i / CHUNK_AREA) | 0;
      // random ticks this crop got: integer part + a coin for the fraction
      let k = Math.floor(expected);
      if (this.rnd() < expected - k) k++;
      if (k <= 0) continue;
      const wx = cx * CHUNK_SIZE + lx, wz = cz * CHUNK_SIZE + lz;
      if (IS_CROP[blocks[i]] === 2) this.farmlandTick(wx, y, wz);
      else if (IS_CROP[blocks[i]] === 3) this.saplingTicks(wx, y, wz, k);
      else this.randomTicks(wx, y, wz, k);
    }
  }

  /** Apply `count` random ticks to the crop at (x, y, z). Returns the final block id. */
  randomTicks(x: number, y: number, z: number, count: number): number {
    const w = this.host.world;
    let id = w.getBlock(x, y, z);
    if (!isCrop(id)) return id;
    if (!this.lightOk(x, y, z)) return id;
    const below = w.getBlock(x, y - 1, z);
    if (!canSustainCrop(below)) return id;
    let around = 0;
    for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) {
      if (dx || dz) around += farmlandWeight(w.getBlock(x + dx, y - 1, z + dz));
    }
    const p = growChance(growthSpeed(farmlandWeight(below), around));
    let next = id;
    for (let i = 0; i < count; i++) {
      if (this.rnd() >= p) continue;
      const n = nextStage(next);
      if (!n) break;
      next = n;
    }
    if (next !== id && this.host.canEdit(x, y, z)) { this.host.setBlock(x, y, z, next); id = next; }
    return id;
  }

  /** SaplingBlock.randomTick x `count`: with light >= 9 each tick has a 1/7 chance of a growth event. Returns true when a tree grew. */
  saplingTicks(x: number, y: number, z: number, count: number): boolean {
    const w = this.host.world;
    if (w.getBlock(x, y, z) !== OAK_SAPLING_ID || !this.host.canEdit(x, y, z)) return false;
    if (Math.max(w.getSky(x, y + 1, z), w.getBlockLight(x, y + 1, z)) < SAPLING_MIN_LIGHT) return false;
    let events = 0;
    for (let i = 0; i < count; i++) if (this.rnd() < SAPLING_TICK_CHANCE) events++;
    return events > 0 && saplingEvents({ getBlock: (a, b, c) => w.getBlock(a, b, c), setBlock: (a, b, c, id) => this.host.setBlock(a, b, c, id), canSet: (a, b, c) => this.host.canEdit(a, b, c) }, x, y, z, events, this.rnd);
  }

  /**
   * FarmBlock.randomTick: water within 4 blocks (same level or one up) keeps
   * it moist; otherwise moist dries, and dry farmland with no crop on it
   * reverts to dirt. (Vanilla steps moisture 7..0; two states here.)
   */
  farmlandTick(x: number, y: number, z: number): number {
    const w = this.host.world;
    const id = w.getBlock(x, y, z);
    if (!isFarmland(id)) return id;
    let to = id;
    if (nearWater(w, x, y, z) || weather.isRainingAt(x, y + 1, z)) to = FARMLAND_ID;
    else if (id === FARMLAND_ID) to = FARMLAND_DRY_ID;
    else if (!isCrop(w.getBlock(x, y + 1, z))) to = DIRT_ID;
    if (to !== id && this.host.canEdit(x, y, z)) this.host.setBlock(x, y, z, to);
    return to;
  }

  private lightOk(x: number, y: number, z: number): boolean {
    const w = this.host.world;
    if (y + 1 >= WORLD_HEIGHT) return true;
    // vanilla uses raw brightness (sky light ignores time of day for this check)
    return Math.max(w.getSky(x, y, z), w.getBlockLight(x, y, z)) >= MIN_GROW_LIGHT;
  }
}

/** Hoe on a block: the id it becomes (farmland), or 0 when it can't be tilled. */
export function tillResult(world: World, x: number, y: number, z: number, face: number): number {
  const id = world.getBlock(x, y, z);
  if (id !== GRASS_ID && id !== DIRT_ID && id !== DIRT_PATH_ID) return 0;
  if (face === -1) return 0; // bottom face never tills
  if (y + 1 < WORLD_HEIGHT && world.getBlock(x, y + 1, z) !== AIR) return 0;
  return tilledFarmland(world, x, y, z);
}

/** Seed item used on the top face of (x, y, z): crop block to place above, or 0. */
export function plantResult(world: World, itemId: number, x: number, y: number, z: number, ny: number): number {
  const crop = plantedBy(itemId);
  if (!crop || ny !== 1) return 0;
  if (!canSustainCrop(world.getBlock(x, y, z))) return 0;
  if (y + 1 >= WORLD_HEIGHT || world.getBlock(x, y + 1, z) !== AIR) return 0;
  return crop;
}

/** Water within 4 blocks horizontally, at the farmland's level or one above (vanilla isNearWater). */
export function nearWater(w: World, x: number, y: number, z: number): boolean {
  for (let dy = 0; dy <= 1; dy++) for (let dx = -4; dx <= 4; dx++) for (let dz = -4; dz <= 4; dz++) {
    if (IS_WATERY[w.getBlock(x + dx, y + dy, z + dz)] === 1) return true;
  }
  return false;
}

/** New farmland is moist straight away when water is near (vanilla sets moisture on the first tick). */
export function tilledFarmland(w: World, x: number, y: number, z: number): number {
  return nearWater(w, x, y, z) ? FARMLAND_ID : FARMLAND_DRY_ID;
}

/**
 * FarmBlock.fallOn: an entity landing on farmland after falling `fall` blocks
 * tramples it to dirt with chance fall - 0.5, if it is big enough
 * (width^2 * height > 0.512). Sneaking players never trample. Returns true
 * when the block was turned to dirt (a crop on it pops off via the caller).
 */
export function trampleCheck(w: World, x: number, y: number, z: number, fall: number, width: number, height: number,
  rnd: () => number): boolean {
  const bx = Math.floor(x), by = Math.floor(y - 0.1), bz = Math.floor(z);
  if (!isFarmland(w.getBlock(bx, by, bz))) return false;
  if (width * width * height <= 0.512) return false;
  return rnd() < fall - 0.5;
}

/**
 * Bone meal used on (x, y, z). Crops age 2-5 stages; grass blocks sprout
 * short grass (and now and then a flower) on grass around them in a 7x7
 * area. Returns the cells that changed (for the green particles), empty when
 * nothing happened (the item is not used up then).
 */
export function boneMeal(host: CropHost, x: number, y: number, z: number, rnd: () => number): [number, number, number][] {
  const w = host.world;
  const id = w.getBlock(x, y, z);
  if (isCrop(id)) {
    if (isMatureCrop(id)) return [];
    const n = boneMealStages(id, rnd);
    if (n > 0 && host.canEdit(x, y, z)) host.setBlock(x, y, z, advance(id, n));
    return [[x, y, z]];
  }
  if (id === OAK_SAPLING_ID) {
    // SaplingBlock bone meal: always used up, 45 % advance the tree (vanilla: two advances grow it; see sapling.ts)
    if (!host.canEdit(x, y, z)) return [];
    if (rnd() < SAPLING_BONEMEAL_CHANCE) saplingEvents({ getBlock: (a, b, c) => w.getBlock(a, b, c), setBlock: (a, b, c, i) => host.setBlock(a, b, c, i), canSet: (a, b, c) => host.canEdit(a, b, c) }, x, y, z, 1, rnd);
    return [[x, y, z]];
  }
  if (id === GRASS_ID) {
    if (y + 1 >= WORLD_HEIGHT || w.getBlock(x, y + 1, z) !== AIR) return [];
    const out: [number, number, number][] = [];
    // GrassBlock.performBonemeal, simplified: 64 tries scattered over 7x7, +-1 in height
    for (let i = 0; i < 64; i++) {
      const gx = x + Math.floor(rnd() * 7) - 3, gz = z + Math.floor(rnd() * 7) - 3;
      for (let dy = 1; dy >= -1; dy--) {
        const gy = y + dy;
        if (w.getBlock(gx, gy, gz) !== GRASS_ID || w.getBlock(gx, gy + 1, gz) !== AIR) continue;
        if (!host.canEdit(gx, gy + 1, gz)) break;
        const plant = rnd() < 0.125 ? (rnd() < 0.5 ? POPPY_ID : DANDELION_ID) : TALL_GRASS_ID;
        host.setBlock(gx, gy + 1, gz, plant);
        out.push([gx, gy + 1, gz]);
        break;
      }
    }
    return out.length ? out : [[x, y + 1, z]];
  }
  return [];
}

// automation handle for scripts/visual/crops.mjs (dev / visual builds only)
if (typeof window !== 'undefined' && (import.meta.env?.DEV || import.meta.env?.MODE === 'visual')) {
  (window as unknown as Record<string, unknown>).__ocCrops = {
    farmland: FARMLAND_ID, dry: FARMLAND_DRY_ID,
    rows: [WHEAT_STAGE_IDS_ALL(), CARROT_STAGE_IDS, POTATO_STAGE_IDS, BEETROOT_STAGE_IDS, NETHER_WART_IDS],
  };
}
function WHEAT_STAGE_IDS_ALL(): number[] { return [...WHEAT_STAGE_IDS, WHEAT_ID]; }
